import { useCallback, useMemo } from 'react'
import { Text, View } from 'react-native'
import { ScrollView } from 'react-native-gesture-handler'
import { useRouter } from 'expo-router'
import { useTranslation } from 'react-i18next'
import { ArrowRight, Check, ChevronRight, Plus } from '@/components/ui/icons'
import { Button } from '@/components/ui/Button'
import { EmptyState } from '@/components/ui/EmptyState'
import { Pressable } from '@/components/ui/Pressable'
import { ScreenContainer } from '@/components/ui/ScreenContainer'
import { GlassSurface } from '@/components/ui/GlassSurface'
import { useServiceRequests } from '@/lib/hooks/use-services'
import {
  SERVICE_CATALOG,
  serviceCategoryById,
  type ServiceCatalogEntry,
  type ServiceRequest,
} from '@/lib/services/services-catalog'
import { useTheme } from '@/lib/hooks/use-theme'
import { FONTS, RADIUS, SPACING } from '@/lib/brand'
import { INTERACTION, useSemanticColors } from '@/lib/theme/tokens'
import { ServiceCategoryIcon } from './ServiceCategoryIcon'

function isOpenRequest(request: ServiceRequest): boolean {
  return !['completed', 'cancelled'].includes(request.status)
}

function categoryForRequest(request: ServiceRequest): ServiceCatalogEntry {
  return serviceCategoryById(request.category)
}

function ServiceCategoryCard({
  category,
  onPress,
}: {
  category: ServiceCatalogEntry
  onPress: () => void
}) {
  const { t } = useTranslation()
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)
  return (
    <Pressable
      onPress={onPress}
      accessibilityRole="button"
      accessibilityLabel={t(category.actionKey)}
      testID={`services.category.${category.id}`}
      style={({ pressed }) => ({
        width: '48%',
        opacity: pressed ? 0.86 : 1,
      })}
    >
      <GlassSurface
        tone="regular"
        radius={RADIUS.xl}
        style={{ minHeight: 166 }}
        contentStyle={{ padding: SPACING.md, justifyContent: 'space-between' }}
      >
        <View style={{ alignItems: 'flex-start', gap: SPACING.sm }}>
          <ServiceCategoryIcon category={category} />
          <View>
            <Text
              numberOfLines={2}
              style={{
                fontFamily: FONTS.bodyBold,
                fontSize: 15,
                lineHeight: 20,
                color: colors.textPrimary,
              }}
            >
              {t(category.titleKey)}
            </Text>
            <Text
              numberOfLines={2}
              style={{
                marginTop: SPACING.xs,
                fontFamily: FONTS.body,
                fontSize: 12,
                lineHeight: 17,
                color: colors.textSecondary,
              }}
            >
              {t(category.descriptionKey)}
            </Text>
          </View>
        </View>
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: SPACING.xs }}>
          <Text style={{ fontFamily: FONTS.bodyBold, fontSize: 12, color: colors.textAccent }}>
            {t(category.badgeKey)}
          </Text>
          <ChevronRight size={15} color={colors.textAccent} strokeWidth={2.2} />
        </View>
      </GlassSurface>
    </Pressable>
  )
}

function ActiveRequestCard({ request, onPress }: { request: ServiceRequest; onPress: () => void }) {
  const { t } = useTranslation()
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)
  const category = categoryForRequest(request)
  return (
    <Pressable
      onPress={onPress}
      accessibilityRole="button"
      accessibilityLabel={t('services.open_request_accessibility', {
        category: t(category.titleKey),
      })}
      testID={`services.request.${request.id}`}
      style={({ pressed }) => ({
        opacity: pressed ? 0.84 : 1,
      })}
    >
      <GlassSurface
        tone="tint"
        tintColor={isDark ? '#6C4C9B' : '#E7D9FA'}
        radius={RADIUS.xl}
        contentStyle={{
          padding: SPACING.md,
          flexDirection: 'row',
          alignItems: 'center',
          gap: SPACING.sm,
        }}
      >
        <ServiceCategoryIcon category={category} size={20} />
        <View style={{ flex: 1, minWidth: 0 }}>
          <Text
            numberOfLines={1}
            style={{ fontFamily: FONTS.bodyBold, fontSize: 14, color: colors.textPrimary }}
          >
            {t(category.titleKey)}
          </Text>
          <Text
            numberOfLines={1}
            style={{
              marginTop: 2,
              fontFamily: FONTS.body,
              fontSize: 12,
              color: colors.textSecondary,
            }}
          >
            {t(`services.status.${request.status}`)} · {request.location.label}
          </Text>
        </View>
        <ChevronRight size={18} color={colors.textSecondary} strokeWidth={2} />
      </GlassSurface>
    </Pressable>
  )
}

export function ServicesHome() {
  const { t } = useTranslation()
  const router = useRouter()
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)
  const requestsQuery = useServiceRequests()
  const activeRequest = useMemo(
    () => (requestsQuery.data ?? []).find(isOpenRequest) ?? null,
    [requestsQuery.data],
  )

  const openRequest = useCallback(
    (category?: ServiceCatalogEntry) => {
      const params = category ? `?category=${category.id}` : ''
      router.push(`/services/request${params}` as never)
    },
    [router],
  )

  return (
    <ScreenContainer variant="content" scroll={false}>
      <ScrollView
        contentContainerStyle={{
          padding: SPACING.lg,
          paddingBottom: SPACING.xl + SPACING.lg,
          gap: SPACING.xl,
        }}
        showsVerticalScrollIndicator={false}
      >
        <GlassSurface
          tone="tint"
          tintColor={isDark ? '#644285' : '#E7D9FA'}
          radius={RADIUS.xl}
          style={{ minHeight: 224 }}
          contentStyle={{
            padding: SPACING.lg,
            // This is the entry point to an operational flow, not a campaign
            // billboard. A compact, editorial hero preserves the promise and
            // gets the first actionable category above the fold on common
            // Android phones.
            justifyContent: 'space-between',
          }}
        >
          <View
            pointerEvents="none"
            style={{
              position: 'absolute',
              width: 190,
              height: 190,
              borderRadius: 95,
              top: -84,
              right: -62,
              backgroundColor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(255,255,255,0.60)',
            }}
          />
          <View style={{ maxWidth: 310, gap: SPACING.sm }}>
            <View
              style={{
                alignSelf: 'flex-start',
                paddingHorizontal: SPACING.sm,
                paddingVertical: SPACING.xs,
                borderRadius: RADIUS.full,
                backgroundColor: isDark ? 'rgba(255,255,255,0.12)' : 'rgba(91,84,144,0.10)',
              }}
            >
              <Text style={{ fontFamily: FONTS.bodyBold, fontSize: 12, color: colors.textPrimary }}>
                {t('services.hero_eyebrow')}
              </Text>
            </View>
            <Text
              style={{
                // Heavitas is intentionally display-only and has uppercase
                // glyphs. It made this reassuring sentence read like an alert
                // and consumed three loud lines on the Redmi. The shared body
                // bold face keeps sentence case and aligns with the request
                // form and tracking view beneath it.
                fontFamily: FONTS.bodyBold,
                fontSize: 28,
                lineHeight: 34,
                color: colors.textPrimary,
                letterSpacing: -0.35,
              }}
            >
              {t('services.hero_title')}
            </Text>
            <Text
              style={{
                fontFamily: FONTS.body,
                fontSize: 14,
                lineHeight: 20,
                color: colors.textSecondary,
              }}
            >
              {t('services.hero_body')}
            </Text>
          </View>
          <Button
            title={t('services.hero_cta')}
            size="md"
            onPress={() => openRequest()}
            icon={<Plus size={18} />}
            testID="services.create-request"
            style={{ alignSelf: 'flex-start', marginTop: SPACING.md }}
          />
        </GlassSurface>

        {activeRequest ? (
          <View style={{ gap: SPACING.sm }}>
            <Text style={{ fontFamily: FONTS.bodyBold, fontSize: 18, color: colors.textPrimary }}>
              {t('services.active_request_title')}
            </Text>
            <ActiveRequestCard
              request={activeRequest}
              onPress={() => router.push(`/services/${activeRequest.id}` as never)}
            />
          </View>
        ) : null}

        <View style={{ gap: SPACING.sm }}>
          <View style={{ gap: SPACING.xs }}>
            <Text style={{ fontFamily: FONTS.bodyBold, fontSize: 20, color: colors.textPrimary }}>
              {t('services.category_title')}
            </Text>
            <Text style={{ fontFamily: FONTS.body, fontSize: 14, color: colors.textSecondary }}>
              {t('services.category_subtitle')}
            </Text>
          </View>
          <View
            style={{
              flexDirection: 'row',
              flexWrap: 'wrap',
              justifyContent: 'space-between',
              gap: SPACING.sm,
            }}
          >
            {SERVICE_CATALOG.map((category) => (
              <ServiceCategoryCard
                key={category.id}
                category={category}
                onPress={() => openRequest(category)}
              />
            ))}
          </View>
        </View>

        <View
          style={{
            gap: SPACING.md,
            borderTopWidth: 1,
            borderTopColor: colors.border,
            paddingTop: SPACING.lg,
          }}
        >
          <Text style={{ fontFamily: FONTS.bodyBold, fontSize: 18, color: colors.textPrimary }}>
            {t('services.how_it_works_title')}
          </Text>
          {['tell_us', 'we_coordinate', 'you_confirm'].map((step, index) => (
            <View
              key={step}
              style={{ flexDirection: 'row', alignItems: 'flex-start', gap: SPACING.sm }}
            >
              <View
                style={{
                  width: INTERACTION.minTouchTarget,
                  height: INTERACTION.minTouchTarget,
                  borderRadius: RADIUS.full,
                  backgroundColor: isDark ? 'rgba(91,84,144,0.24)' : 'rgba(91,84,144,0.10)',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {index === 2 ? (
                  <Check size={19} color={colors.textBrand} strokeWidth={2.3} />
                ) : (
                  <Text style={{ fontFamily: FONTS.bodyBold, color: colors.textBrand }}>
                    {index + 1}
                  </Text>
                )}
              </View>
              <View style={{ flex: 1, paddingTop: SPACING.xs }}>
                <Text
                  style={{ fontFamily: FONTS.bodyBold, fontSize: 14, color: colors.textPrimary }}
                >
                  {t(`services.how_it_works.${step}.title`)}
                </Text>
                <Text
                  style={{
                    marginTop: 2,
                    fontFamily: FONTS.body,
                    fontSize: 13,
                    lineHeight: 18,
                    color: colors.textSecondary,
                  }}
                >
                  {t(`services.how_it_works.${step}.body`)}
                </Text>
              </View>
            </View>
          ))}
        </View>

        {requestsQuery.isError ? (
          <EmptyState
            variant="aurora"
            title={t('services.requests_load_error_title')}
            description={t('services.requests_load_error_body')}
            action={{ label: t('common.retry'), onPress: () => void requestsQuery.refetch() }}
          />
        ) : null}

        <Pressable
          onPress={() => openRequest()}
          accessibilityRole="button"
          accessibilityLabel={t('services.secondary_cta')}
          testID="services.secondary-create-request"
          style={({ pressed }) => ({
            minHeight: INTERACTION.minTouchTarget,
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'center',
            gap: SPACING.xs,
            opacity: pressed ? 0.72 : 1,
          })}
        >
          <Text style={{ fontFamily: FONTS.bodyBold, fontSize: 14, color: colors.textAccent }}>
            {t('services.secondary_cta')}
          </Text>
          <ArrowRight size={17} color={colors.textAccent} strokeWidth={2.2} />
        </Pressable>
      </ScrollView>
    </ScreenContainer>
  )
}
